import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Copy, Eye, Link as LinkIcon, Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, Switch } from "@fleet/ui";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { cn, type components } from "@vigia/shared";

type ThemeRow = components["schemas"]["ThemeDTO"];

interface ThemeCardProps {
  theme: ThemeRow;
  onToggle: (id: string, active: boolean) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  isAnyPending: boolean;
  t: (s: MessageDescriptor, v?: Record<string, unknown>) => string;
}

const ID_PREVIEW_LENGTH = 8;

export function ThemeCard(props: ThemeCardProps) {
  const handleToggleChange = (v: boolean) => {
    props.onToggle(props.theme.id, v);
  };

  const handleDuplicateClick = () => {
    props.onDuplicate(props.theme.id);
  };

  const handleDeleteConfirm = () => {
    props.onDelete(props.theme.id);
  };

  return (
    <Card class="group overflow-hidden transition-all hover:border-primary/50 hover:shadow-lg">
      <CardContent class="p-0">
        {/* Preview Strip */}
        <div
          class="h-24 w-full relative flex items-end p-3"
          style={{ "background-color": props.theme.background }}
        >
          <div class="flex gap-2">
            <div
              class="h-8 w-8 rounded-full border-2 border-white/20"
              style={{ "background-color": props.theme.accent }}
            />
            <div
              class="h-8 w-8 rounded-full border-2 border-white/20"
              style={{ "background-color": props.theme.surface }}
            />
          </div>
          <Badge
            variant="secondary"
            class="absolute top-3 right-3 bg-white/10 backdrop-blur-md border-white/20 text-white"
          >
            {props.theme.isDark ? props.t(msg`🌙 Dark`) : props.t(msg`☀️ Light`)}
          </Badge>
        </div>

        <div class="p-4 space-y-4">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <h3 class="font-bold truncate text-lg">{props.theme.name}</h3>
              <p class="text-[10px] text-muted-foreground font-mono">
                {(() => {
                  const id = props.theme.id.slice(0, ID_PREVIEW_LENGTH);
                  return props.t(msg`ID: ${id}...`);
                })()}{" "}
                • {props.t(msg`Order`)}: {props.theme.sortOrder}
              </p>
            </div>
            <Switch
              checked={props.theme.isActive}
              onCheckedChange={handleToggleChange}
              disabled={props.isAnyPending}
            />
          </div>

          <div class="flex items-center justify-between pt-2 border-t">
            <div class="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                class="h-8 w-8"
                onClick={handleDuplicateClick}
                disabled={props.isAnyPending}
              >
                <Copy class="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" class="h-8 w-8">
                <LinkIcon
                  class={cn(
                    "h-4 w-4",
                    props.theme.pairId ? "text-primary" : "text-muted-foreground opacity-50",
                  )}
                />
              </Button>
            </div>
            <div class="flex gap-1">
              <Button variant="ghost" size="icon" class="h-8 w-8">
                <Eye class="h-4 w-4" />
              </Button>
              <ConfirmDialog
                title={props.t(msg`Delete theme?`)}
                description={(() => {
                  const { name } = props.theme;
                  return props.t(
                    msg`Are you sure you want to delete the theme "${name}"? This action cannot be undone.`,
                  );
                })()}
                confirmLabel={props.t(msg`Delete`)}
                variant="destructive"
                onConfirm={handleDeleteConfirm}
                renderTrigger={(open) => (
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={open}
                    disabled={props.isAnyPending}
                  >
                    <Trash2 class="h-4 w-4" />
                  </Button>
                )}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
